import { Flex, Text, Tag, Button, Icon, iconWeight } from '@nula/core-ui';
import type { PlanCatalogEntry, BillingInterval, SubscriptionStatus } from './types';
import type { AppliedPromo } from './promo-code-input';
import { planCta } from './plan-cta';
import { formatMoney } from './format';
import styles from './plan-catalog-card.module.css';

interface PlanCatalogCardProps {
  plan: PlanCatalogEntry;
  subscription: SubscriptionStatus;
  interval: BillingInterval;
  /** "Everything in <lower plan>, and:" line, derived from plan ordering. */
  includesLabel?: string;
  recommended?: boolean;
  onSelect: (plan: PlanCatalogEntry) => void;
  /** When set, the original price strikes through and the discounted price shows beside it. */
  appliedPromo?: AppliedPromo | null;
}

/**
 * The discounted invoice total, given the plan's ordinary invoice total.
 *
 * Both discount kinds are per-invoice: Stripe deducts `amount_off` once per
 * invoice, which on an annual plan is once per YEAR off the full-year total.
 * The result must therefore be computed on the invoice total and only then
 * scaled to a per-month figure by the caller — subtracting the coupon from an
 * already-divided per-month rate overstates an annual discount ~12x.
 */
function discountedInvoiceCents(amountCents: number, promo: AppliedPromo): number {
  if (promo.percentOff !== null) {
    return Math.round(amountCents * (1 - promo.percentOff / 100));
  }
  if (promo.amountOffCents !== null) {
    return Math.max(0, amountCents - promo.amountOffCents);
  }
  return amountCents;
}

const FREE = 'basic';

function isFreePlan(plan: PlanCatalogEntry): boolean {
  return plan.code === FREE || (plan.prices.length === 0 && plan.monthlyCredits === 0);
}

function perMonthCents(invoiceCents: number, interval: BillingInterval): number {
  return interval === 'year' ? Math.round(invoiceCents / 12) : invoiceCents;
}

/**
 * The per-month price for a paid plan at the given interval, or null when the
 * amount can't be resolved from Stripe (missing/misconfigured price). The card
 * labels the price "/mo", so annual prices (Stripe stores the full-year total)
 * are divided by 12 to show the effective monthly rate. Callers MUST
 * distinguish this null (price-unavailable → "—") from a free plan (→ "Free").
 */
function paidPriceCents(
  plan: PlanCatalogEntry,
  interval: BillingInterval,
): { cents: number; invoiceCents: number; currency: string | null } | null {
  const entry = plan.prices.find((p) => p.interval === interval);
  if (!entry || entry.amountCents === null) return null;
  return {
    cents: perMonthCents(entry.amountCents, interval),
    invoiceCents: entry.amountCents,
    currency: entry.currency,
  };
}

export function PlanCatalogCard({
  plan,
  subscription,
  interval,
  includesLabel,
  recommended,
  onSelect,
  appliedPromo,
}: PlanCatalogCardProps) {
  const cta = planCta(plan, subscription);
  const isCurrent = plan.code === subscription.planCode;
  const trialing = isCurrent && subscription.status === 'trialing';
  const free = isFreePlan(plan);
  const priceEntry = free ? null : paidPriceCents(plan, interval);
  const price = priceEntry ? formatMoney(priceEntry.cents, priceEntry.currency) : null;
  const discountedPrice =
    priceEntry && appliedPromo
      ? formatMoney(
          perMonthCents(discountedInvoiceCents(priceEntry.invoiceCents, appliedPromo), interval),
          priceEntry.currency,
        )
      : null;
  const showRibbon = recommended && !isCurrent;

  return (
    <Flex vertical gap="sm" className={styles.card} data-current={isCurrent || undefined}>
      {showRibbon && <span className={styles.ribbon}>Most popular</span>}

      <Flex align="center" justify="space-between" className={styles.head}>
        <Text size="lg" className={styles.name}>
          {plan.name}
        </Text>
        {isCurrent && !trialing && <Tag color="success">Current</Tag>}
      </Flex>

      {trialing && (
        <Text size="sm" className={styles.trialNote}>
          Trial active. Add a card to keep {plan.name} when it ends.
        </Text>
      )}

      <Text className={styles.price}>
        {free ? (
          'Free'
        ) : discountedPrice ? (
          <>
            <Text del type="secondary" className={styles.strikePrice}>
              {price}
            </Text>
            {discountedPrice}
            <span className={styles.per}> /mo</span>
          </>
        ) : price ? (
          <>
            {price}
            <span className={styles.per}> /mo</span>
          </>
        ) : (
          '—'
        )}
      </Text>

      {includesLabel && (
        <Text size="sm" type="secondary">
          {includesLabel}
        </Text>
      )}

      <Flex vertical gap="xs" className={styles.features}>
        {plan.features.map((f) => (
          <Flex key={f} gap="xs" align="start">
            <Icon name="check" size={16} weight={iconWeight.regular} className={styles.check} />
            <Text size="sm">{f}</Text>
          </Flex>
        ))}
      </Flex>

      <div className={styles.cta}>
        <Button
          variant={cta.primary ? 'primary' : 'default'}
          pressEffect={cta.primary ? 'glow' : undefined}
          disabled={cta.disabled}
          block
          onClick={() => onSelect(plan)}
        >
          {cta.label}
        </Button>
      </div>
    </Flex>
  );
}
